import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';

class HmMegaMenu extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#syncMenuBlocks();
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keyup', this.#handleKeyUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keyup', this.#handleKeyUp);
    if (this.hasAttribute('open')) {
      document.documentElement.removeAttribute('scroll-lock');
      removeTrapFocus();
    }
  }

  get #openButton() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-hm-open]'));
  }

  get #overlay() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.hm-mega-menu__overlay'));
  }

  get #detailHost() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-hm-details]'));
  }

  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const openButton = target.closest('[data-hm-open]');
    if (openButton) {
      event.preventDefault();
      this.open();
      return;
    }

    const closeButton = target.closest('[data-hm-close]');
    if (closeButton) {
      event.preventDefault();
      this.close();
      return;
    }

    const tabButton = target.closest('[data-hm-tab]');
    if (tabButton instanceof HTMLElement) {
      event.preventDefault();
      this.#activateTab(tabButton.dataset.hmTab ?? '0');
      return;
    }

    const detailButton = target.closest('[data-hm-detail]');
    if (detailButton instanceof HTMLElement) {
      event.preventDefault();
      this.#openDetail(detailButton.dataset.hmDetail ?? '');
      return;
    }

    const backButton = target.closest('[data-hm-back]');
    if (backButton) {
      event.preventDefault();
      this.#closeActiveDetail();
    }
  };

  #handleKeyUp = (event) => {
    if (event.key === 'Escape') {
      this.close();
    }
  };

  open() {
    const overlay = this.#overlay;
    if (!overlay) return;

    this.#syncMenuBlocks();
    overlay.hidden = false;
    this.removeAttribute('closing');
    this.#openButton?.setAttribute('aria-expanded', 'true');
    document.documentElement.setAttribute('scroll-lock', '');
    this.#closeDetails();

    // Let the browser paint the off-canvas state before transitioning to open.
    requestAnimationFrame(() => {
      this.setAttribute('open', '');
      this.setAttribute('animating-open', '');

      window.setTimeout(() => {
        this.removeAttribute('animating-open');
      }, 760);

      requestAnimationFrame(() => {
        trapFocus(overlay);
        const selectedTab = this.querySelector('[data-hm-tab][aria-selected="true"]');
        if (selectedTab instanceof HTMLElement) selectedTab.focus();
      });
    });
  }

  close() {
    const overlay = this.#overlay;
    if (!overlay) return;

    this.removeAttribute('open');
    this.removeAttribute('animating-open');
    this.setAttribute('closing', '');
    this.#openButton?.setAttribute('aria-expanded', 'false');
    document.documentElement.removeAttribute('scroll-lock');
    removeTrapFocus();

    window.setTimeout(() => {
      if (!this.hasAttribute('open')) {
        overlay.hidden = true;
        this.removeAttribute('closing');
      }
    }, 520);

    this.#openButton?.focus();
  }

  /**
   * @param {string} tabId
   */
  #activateTab(tabId) {
    for (const tab of this.querySelectorAll('[data-hm-tab]')) {
      tab.setAttribute('aria-selected', `${tab instanceof HTMLElement && tab.dataset.hmTab === tabId}`);
    }

    for (const panel of this.querySelectorAll('[data-hm-panel]')) {
      if (!(panel instanceof HTMLElement)) continue;
      panel.hidden = panel.dataset.hmPanel !== tabId;
    }

    this.#syncMenuBlocks();
    this.#closeDetails();
  }

  #syncMenuBlocks() {
    const localeRoot = this.#localeRoot;

    for (const panel of this.querySelectorAll('[data-hm-panel]')) {
      if (!(panel instanceof HTMLElement)) continue;

      const panelTargets = this.#getTargetCandidates(
        [panel.dataset.hmPanelTarget ?? '', panel.dataset.hmPanelUrl ?? '', panel.dataset.hmPanelHandle ?? ''],
        localeRoot
      );
      const activeSubmenuTargets = new Set();
      let hasMatchingPromo = false;

      for (const submenuBlock of panel.querySelectorAll('.hm-mega-menu__submenus [data-hm-block-type="submenu"]')) {
        if (!(submenuBlock instanceof HTMLElement)) continue;

        const submenuTargets = this.#getTargetCandidates(
          [
            submenuBlock.dataset.hmSubmenuTarget ?? '',
            submenuBlock.dataset.hmSubmenuTargetHandle ?? '',
            submenuBlock.dataset.hmSubmenuMenuHandle ?? '',
          ],
          localeRoot
        );

        if (this.#targetSetsIntersect(panelTargets, submenuTargets)) {
          for (const candidate of submenuTargets) activeSubmenuTargets.add(candidate);
        }
      }

      for (const block of panel.querySelectorAll('[data-hm-block-type]')) {
        if (!(block instanceof HTMLElement)) continue;

        const blockType = block.dataset.hmBlockType;
        const isPromoContainer = Boolean(block.closest('.hm-mega-menu__mobile-promos, .hm-mega-menu__desktop-promos'));
        const isSubmenuContainer = Boolean(block.closest('.hm-mega-menu__submenus'));
        let isActiveBlock = false;
        const matchesPanel = (targets) => {
          const blockTargets = this.#getTargetCandidates(targets, localeRoot);

          return this.#targetSetsIntersect(panelTargets, blockTargets);
        };

        if (blockType === 'submenu') {
          isActiveBlock =
            isSubmenuContainer &&
            matchesPanel([
              block.dataset.hmSubmenuTarget ?? '',
              block.dataset.hmSubmenuTargetHandle ?? '',
              block.dataset.hmSubmenuMenuHandle ?? '',
            ]);
        }

        if (blockType === 'promo') {
          const promoTargets = this.#getTargetCandidates(
            [block.dataset.hmPromoTarget ?? '', block.dataset.hmPromoTargetHandle ?? ''],
            localeRoot
          );
          isActiveBlock =
            isPromoContainer &&
            (this.#targetSetsIntersect(panelTargets, promoTargets) ||
              this.#targetSetsIntersect(activeSubmenuTargets, promoTargets));
          if (isActiveBlock) hasMatchingPromo = true;
        }

        block.hidden = !isActiveBlock;
        block.toggleAttribute('data-hm-block-active', isActiveBlock);
      }

      for (const promoContainer of panel.querySelectorAll('.hm-mega-menu__mobile-promos, .hm-mega-menu__desktop-promos')) {
        if (promoContainer instanceof HTMLElement) {
          promoContainer.hidden = !hasMatchingPromo;
        }
      }
    }
  }

  get #localeRoot() {
    const root =
      this.dataset.hmRootUrl ||
      document.documentElement.dataset.root ||
      document.documentElement.getAttribute('data-root') ||
      '';

    const localeRoot = this.#normalizePath(root).split('/')[0] ?? '';
    if (localeRoot) return localeRoot;

    const pathLocale = window.location.pathname.replace(/^\/+/, '').split('/')[0] ?? '';

    return this.#isLocaleSegment(pathLocale) ? pathLocale : '';
  }

  /**
   * @param {string[]} values
   * @param {string} localeRoot
   */
  #getTargetCandidates(values, localeRoot) {
    const candidates = new Set();

    for (const value of values) {
      this.#addTargetCandidate(candidates, this.#normalizePath(value));
      this.#addTargetCandidate(candidates, this.#normalizePath(value, localeRoot));

      for (const localeAgnosticValue of this.#getLocaleAgnosticPaths(value, localeRoot)) {
        this.#addTargetCandidate(candidates, localeAgnosticValue);
      }
    }

    return candidates;
  }

  /**
   * @param {Set<string>} firstSet
   * @param {Set<string>} secondSet
   */
  #targetSetsIntersect(firstSet, secondSet) {
    for (const candidate of secondSet) {
      if (firstSet.has(candidate)) return true;
    }

    return false;
  }

  /**
   * @param {Set<string>} candidates
   * @param {string} value
   */
  #addTargetCandidate(candidates, value) {
    if (!value) return;

    candidates.add(value);
    const normalizedHandle = this.#handleize(value);
    if (normalizedHandle) candidates.add(normalizedHandle);

    const segments = value.split('/').filter(Boolean);
    const lastSegment = segments.at(-1);
    if (lastSegment) {
      candidates.add(lastSegment);
      const lastSegmentHandle = this.#handleize(lastSegment);
      if (lastSegmentHandle) candidates.add(lastSegmentHandle);
    }
  }

  /**
   * @param {string} value
   * @param {string} localeRoot
   */
  #getLocaleAgnosticPaths(value, localeRoot) {
    const normalizedValue = this.#normalizePath(value);
    const paths = new Set([normalizedValue]);
    const normalizedLocaleRoot = this.#normalizePath(localeRoot);
    const segments = normalizedValue.split('/').filter(Boolean);
    const firstSegment = segments[0] ?? '';

    if (normalizedLocaleRoot && normalizedLocaleRoot !== '/') {
      paths.add(this.#normalizePath(normalizedValue, normalizedLocaleRoot));
    }

    if (this.#isLocaleSegment(firstSegment) && segments.length > 1) {
      paths.add(segments.slice(1).join('/'));
    }

    return paths;
  }

  /**
   * @param {string} value
   * @param {string} [localeRoot]
   */
  #normalizePath(value, localeRoot = '') {
    let normalizedValue = value.trim();
    if (!normalizedValue) return '';

    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(normalizedValue)) {
      try {
        normalizedValue = new URL(normalizedValue).pathname;
      } catch (_error) {
        normalizedValue = normalizedValue.split('?')[0]?.split('#')[0] ?? '';
      }
    }

    normalizedValue = normalizedValue.split('?')[0]?.split('#')[0] ?? '';

    if (normalizedValue !== '/') {
      normalizedValue = normalizedValue.replace(/^\/+/, '').replace(/\/+$/, '');
    }

    if (localeRoot && normalizedValue === localeRoot) {
      return '/';
    }

    if (localeRoot && normalizedValue.startsWith(`${localeRoot}/`)) {
      normalizedValue = normalizedValue.slice(localeRoot.length + 1);
    }

    return normalizedValue || '/';
  }

  /**
   * @param {string} value
   */
  #handleize(value) {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * @param {string} value
   */
  #isLocaleSegment(value) {
    return /^[a-z]{2}(?:-[a-z]{2})?$/i.test(value);
  }

  /**
   * @param {string} detailId
   */
  #openDetail(detailId) {
    this.removeAttribute('animating-open');
    this.setAttribute('detail-open', '');
    this.#freezeParentPanel();
    this.#ensureDetailPanel(detailId);

    for (const detailPanel of this.querySelectorAll('[data-hm-detail-panel]')) {
      if (!(detailPanel instanceof HTMLElement)) continue;
      detailPanel.hidden = detailPanel.dataset.hmDetailPanel !== detailId;
      detailPanel.toggleAttribute('data-active', detailPanel.dataset.hmDetailPanel === detailId);
      detailPanel.removeAttribute('data-closing');
    }

    const activePanel = this.querySelector(`[data-hm-detail-panel="${CSS.escape(detailId)}"]`);
    if (activePanel instanceof HTMLElement) {
      requestAnimationFrame(() => {
        const backButton = activePanel.querySelector('[data-hm-back]');
        if (backButton instanceof HTMLElement) backButton.focus();
      });
    }
  }

  /**
   * @param {string} detailId
   */
  #ensureDetailPanel(detailId) {
    if (this.querySelector(`[data-hm-detail-panel="${CSS.escape(detailId)}"]`)) return;

    const activePanel = this.querySelector('[data-hm-panel]:not([hidden])');
    const template =
      activePanel?.querySelector(`template[data-hm-detail-template="${CSS.escape(detailId)}"]`) ??
      this.querySelector(`template[data-hm-detail-template="${CSS.escape(detailId)}"]`);

    if (!(template instanceof HTMLTemplateElement)) return;

    const detailPanel = template.content.firstElementChild?.cloneNode(true);
    if (!(detailPanel instanceof HTMLElement)) return;

    this.#detailHost?.append(detailPanel);
  }

  #closeDetails() {
    this.removeAttribute('detail-open');
    this.#unfreezeParentPanel();

    for (const detailPanel of this.querySelectorAll('[data-hm-detail-panel]')) {
      if (detailPanel instanceof HTMLElement) {
        detailPanel.hidden = true;
        detailPanel.removeAttribute('data-active');
        detailPanel.removeAttribute('data-closing');
      }
    }
  }

  #closeActiveDetail() {
    const activePanel = this.querySelector('[data-hm-detail-panel][data-active]');
    if (!(activePanel instanceof HTMLElement)) return;

    activePanel.removeAttribute('data-active');
    activePanel.setAttribute('data-closing', '');

    window.setTimeout(() => {
      if (!activePanel.hasAttribute('data-active')) {
        activePanel.hidden = true;
        activePanel.removeAttribute('data-closing');
        this.removeAttribute('detail-open');
        this.#unfreezeParentPanel();
      }
    }, 360);
  }

  #freezeParentPanel() {
    const activePanel = this.querySelector('[data-hm-panel]:not([hidden])');
    if (!(activePanel instanceof HTMLElement)) return;

    for (const animation of activePanel.getAnimations({ subtree: true })) {
      animation.cancel();
    }

    const parentAnimatedElements = activePanel.querySelectorAll(
      '.hm-mega-menu__animated-item, .hm-mega-menu__mobile-promos, .hm-mega-menu__desktop-promos'
    );

    for (const element of parentAnimatedElements) {
      if (!(element instanceof HTMLElement)) continue;
      element.dataset.hmFrozen = 'true';
      element.style.animation = 'none';
      element.style.opacity = '1';
      element.style.transform = 'none';
    }
  }

  #unfreezeParentPanel() {
    for (const element of this.querySelectorAll('[data-hm-frozen="true"]')) {
      if (!(element instanceof HTMLElement)) continue;
      delete element.dataset.hmFrozen;
      element.style.animation = '';
      element.style.opacity = '';
      element.style.transform = '';
    }
  }
}

if (!customElements.get('hm-mega-menu')) {
  customElements.define('hm-mega-menu', HmMegaMenu);
}
